import React, { useRef, useEffect } from 'react';

const ParticlesBackground = ({ 
    color = '#ffffff', 
    lineColor = 'rgba(255, 255, 255, 0.5)',
    backgroundColor = 'transparent' 
}) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let particles = [];

        const resizeCanvas = () => {
            // Get parent dimensions ensuring canvas fills the container
            const parent = canvas.parentElement;
            if (parent) {
                canvas.width = parent.clientWidth;
                canvas.height = parent.clientHeight;
            } else {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            }
            init(); // Re-init on resize to adjust particle count
        };

        window.addEventListener('resize', resizeCanvas);
        // Initial sizing delay to ensure parent is rendered
        setTimeout(resizeCanvas, 0);

        class Particle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.vx = (Math.random() - 0.5) * 0.5;
                this.vy = (Math.random() - 0.5) * 0.5;
                this.size = Math.random() * 2 + 1;
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;

                if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
                if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
            }

            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.fill();
            }
        }

        const init = () => {
            particles = [];
            // Adjust density: 1 particle per 9000px²
            const density = 9000;
            const numberOfParticles = Math.floor((canvas.width * canvas.height) / density);
            for (let i = 0; i < numberOfParticles; i++) {
                particles.push(new Particle());
            }
        };

        const animate = () => {
            // Clear with transparent or custom background
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Should be handled by CSS parent, but can fill if requested
            // if (backgroundColor !== 'transparent') {
            //     ctx.fillStyle = backgroundColor;
            //     ctx.fillRect(0, 0, canvas.width, canvas.height);
            // }

            particles.forEach(particle => {
                particle.update();
                particle.draw();
            });

            connectParticles();
            animationFrameId = requestAnimationFrame(animate);
        };

        const connectParticles = () => {
            const maxDistance = 120;
            // Parse RGB from hex/rgba color for simpler alpha manipulation if needed
            // For now assuming lineColor is a valid CSS string
            
            for (let a = 0; a < particles.length; a++) {
                for (let b = a; b < particles.length; b++) {
                    const dx = particles[a].x - particles[b].x;
                    const dy = particles[a].y - particles[b].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < maxDistance) {
                        ctx.beginPath();
                        // Opacity based on distance
                        // Extracting alpha is complex without regex, so just using global alpha or assuming white lines
                        // Hacky: assuming line color is whitish and simple
                        
                        const opacity = 1 - (distance / maxDistance);
                        ctx.strokeStyle = lineColor.replace('1)', `${opacity})`).replace('0.5)', `${opacity}`); 
                        // If color string layout differs, fallback to simple
                        if (!lineColor.includes('rgba')) {
                             ctx.globalAlpha = opacity;
                             ctx.strokeStyle = lineColor;
                        }

                        ctx.lineWidth = 1;
                        ctx.moveTo(particles[a].x, particles[a].y);
                        ctx.lineTo(particles[b].x, particles[b].y);
                        ctx.stroke();
                        ctx.globalAlpha = 1; // Reset
                    }
                }
            }
        };

        init();
        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, [color, lineColor]);

    return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none', backgroundColor }} />;
};

export default ParticlesBackground;
